"use client";

import { usePathname } from "next/navigation";
import Tabs, { TAB_STYLE, TabItem } from "@/components/common/Tab";
import SubpageBackground from "@/components/common/SubpageBackground";
import PageTitle from "@/components/common/PageTitle";

const tabs: TabItem[] = [
  {
    id: "ot-ics-simulation",
    name: "OT/ICS Simulation",
    value: "/ot-security/ot-ics-simulation",
    href: "/ot-security/ot-ics-simulation",
  },
  {
    id: "aid-ics-framework",
    name: "AID ICS Framework",
    value: "/ot-security/aid-ics-framework",
    href: "/ot-security/aid-ics-framework",
  },
];

export default function OTSecurityLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  // The title tracks the active tab, like every other tabbed section
  // (Products / Company / Services). Labels match Header.tsx's OT Security menu.
  // Both sub-pages now have their own segment — "/ot-security" itself is a
  // redirect, not a page, so the trailing fallback is unreachable in practice.
  const getTitle = () => {
    if (pathname.includes("ot-ics-simulation")) return "OT/ICS Simulation";
    if (pathname.includes("aid-ics-framework")) return "AID ICS Framework";
    return "OT Security";
  };

  const title = getTitle();

  return (
    <div className="relative min-h-screen flex flex-col bg-background pb-8 sm:pb-[70px]">
      <SubpageBackground />

      <div className="relative z-10 flex flex-col flex-1">
        {/* Gutters: 1300px content inside px-4/6/8 lands on Figma's 70px side
            margin at the 1440px artboard — same wrapper as the Products page. */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <section className="w-full max-w-[1300px] mx-auto pb-6 pt-[112px] md:pt-[128px] md:pb-[48px]">
            {/* Title with red accent bar — Figma SectionName (node 1824:42687):
                5px red left bar + 24px padding. Mirrors the Products header.
                All three titles here fit on one line at 390px, so no mobile
                height needs reserving. */}
            <PageTitle className="mb-4">{title}</PageTitle>

            {/* Shared Tabs component (STYLE_1) — same as the Products page: wraps
                on narrow screens (no scrollbar) with an animated glowing underline.
                First tab hugs the left margin so it lines up with the accent bar. */}
            <div className="nav-tabs-wrapper">
              <Tabs
                tabs={tabs}
                currentValue={pathname}
                style={TAB_STYLE.STYLE_1}
                showContent={false}
                useRouter={true}
                customClassName="[&>a:first-child]:!pl-0"
              />
            </div>
          </section>
        </div>

        <main className="flex-1 w-full">{children}</main>
      </div>

      <style jsx>{`
        /* The title's own transition lives in PageTitle now. What used to sit here
           was a mount entrance animating letter-spacing 0.08em to 0, which
           re-lays out the text every frame — measured as the glyphs spreading
           23.3px over 10 steps — plus a copy of the cross-fade that had already
           drifted out of step with the Products header's. */
        .nav-tabs-wrapper {
          animation: navFadeUp 0.5s ease-out 0.15s both;
        }
        @keyframes navFadeUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .nav-tabs-wrapper {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
